import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Film, Loader2, CheckCircle2, XCircle, Scissors, Trash2 } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";

const STATUS_META = {
  processing: { icon: Loader2, cls: "text-amber-400 animate-spin", label: "AI notes generating..." },
  ready: { icon: CheckCircle2, cls: "text-emerald-400", label: "AI notes ready" },
  failed: { icon: XCircle, cls: "text-rose-400", label: "AI notes failed" },
};

function formatTime(sec) {
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
}

export default function ImportFromRecordingPanel({ courseId, onImported }) {
  const [recordings, setRecordings] = useState(null);
  const [importingId, setImportingId] = useState(null);
  const [detectingId, setDetectingId] = useState(null);
  const [chaptersFor, setChaptersFor] = useState({});
  const [splittingId, setSplittingId] = useState(null);

  useEffect(() => {
    api.get("/meetings/recordings/mine").then((res) => setRecordings(res.data)).catch(() => setRecordings([]));
  }, []);

  const handleImport = async (recording) => {
    setImportingId(recording.id);
    try {
      await api.post(`/courses/${courseId}/videos/from-recording`, { recording_id: recording.id, title: recording.title });
      toast.success("Imported! This recording is now a lecture in your course.");
      onImported();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setImportingId(null);
    }
  };

  const detectChapters = async (recordingId) => {
    setDetectingId(recordingId);
    try {
      const { data } = await api.get(`/meetings/recordings/${recordingId}/chapters`);
      setChaptersFor((prev) => ({ ...prev, [recordingId]: data.chapters.map((c) => ({ ...c, include: true })) }));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Chapter detection failed");
    } finally {
      setDetectingId(null);
    }
  };

  const updateChapterTitle = (recordingId, idx, title) => {
    setChaptersFor((prev) => ({
      ...prev, [recordingId]: prev[recordingId].map((c, i) => (i === idx ? { ...c, title } : c)),
    }));
  };

  const toggleChapter = (recordingId, idx) => {
    setChaptersFor((prev) => ({
      ...prev, [recordingId]: prev[recordingId].map((c, i) => (i === idx ? { ...c, include: !c.include } : c)),
    }));
  };

  const confirmSplit = async (recordingId) => {
    const chapters = (chaptersFor[recordingId] || []).filter((c) => c.include);
    if (chapters.length === 0) { toast.error("Select at least one chapter"); return; }
    setSplittingId(recordingId);
    try {
      const { data } = await api.post(`/courses/${courseId}/videos/from-recording-split`, {
        recording_id: recordingId, chapters: chapters.map(({ title, start, end }) => ({ title, start, end })),
      });
      toast.success(`Imported as ${data.created.length} separate lectures!`);
      setChaptersFor((prev) => { const next = { ...prev }; delete next[recordingId]; return next; });
      onImported();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Split import failed");
    } finally {
      setSplittingId(null);
    }
  };

  if (recordings === null) {
    return <div className="flex items-center justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-pink-400" /></div>;
  }
  if (recordings.length === 0) {
    return (
      <p className="text-sm text-slate-500" data-testid="import-recordings-empty">
        You don't have any Cloudivo Meet recordings yet. Host a live class, hit "Start Recording", and it'll show up here to import.
      </p>
    );
  }

  return (
    <div className="space-y-2" data-testid="import-recordings-list">
      {recordings.map((r) => {
        const meta = STATUS_META[r.transcript_status] || STATUS_META.processing;
        const Icon = meta.icon;
        const chapters = chaptersFor[r.id];
        return (
          <div key={r.id} className="border border-white/10 rounded-lg px-3 py-2.5" data-testid={`import-recording-row-${r.id}`}>
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2.5 min-w-0">
                <Film className="w-4 h-4 text-cyan-400 shrink-0" />
                <div className="min-w-0">
                  <p className="text-sm text-white truncate">{r.title}</p>
                  <span className={`text-[11px] flex items-center gap-1 ${meta.cls.replace("animate-spin", "")}`}>
                    <Icon className={`w-3 h-3 ${meta.cls.includes("animate-spin") ? "animate-spin" : ""}`} /> {meta.label}
                  </span>
                </div>
              </div>
              <div className="shrink-0 flex items-center gap-2">
                {r.transcript_status === "ready" && (
                  <button onClick={() => detectChapters(r.id)} disabled={detectingId === r.id} data-testid={`split-recording-btn-${r.id}`}
                    className="flex items-center gap-1.5 bg-white/5 hover:bg-white/10 border border-white/10 disabled:opacity-60 text-slate-300 text-xs font-semibold px-3 py-1.5 rounded-lg transition-colors">
                    {detectingId === r.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Scissors className="w-3 h-3" />} Split into Lectures
                  </button>
                )}
                <button onClick={() => handleImport(r)} disabled={importingId === r.id} data-testid={`import-recording-btn-${r.id}`}
                  className="flex items-center gap-1.5 bg-pink-500 hover:bg-pink-400 disabled:opacity-60 text-white text-xs font-semibold px-3 py-1.5 rounded-lg transition-colors">
                  {importingId === r.id && <Loader2 className="w-3 h-3 animate-spin" />} Import as 1 Lecture
                </button>
              </div>
            </div>

            {chapters && (
              <div className="mt-3 pt-3 border-t border-white/10 space-y-2" data-testid={`recording-chapters-${r.id}`}>
                <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500">AI-detected chapters — edit titles or uncheck to skip</p>
                {chapters.map((c, idx) => (
                  <div key={idx} className="flex items-center gap-2.5" data-testid={`recording-chapter-row-${r.id}-${idx}`}>
                    <input type="checkbox" checked={c.include} onChange={() => toggleChapter(r.id, idx)} data-testid={`recording-chapter-checkbox-${r.id}-${idx}`} className="accent-pink-500" />
                    <span className="text-[11px] text-slate-500 shrink-0 w-20">{formatTime(c.start)}–{formatTime(c.end)}</span>
                    <input value={c.title} onChange={(e) => updateChapterTitle(r.id, idx, e.target.value)} data-testid={`recording-chapter-title-${r.id}-${idx}`}
                      className="flex-1 bg-white/5 border border-white/10 rounded-md px-2 py-1 text-xs text-white outline-none focus:border-pink-400/50" />
                  </div>
                ))}
                <div className="flex items-center gap-2 pt-1">
                  <button onClick={() => confirmSplit(r.id)} disabled={splittingId === r.id} data-testid={`confirm-split-btn-${r.id}`}
                    className="flex items-center gap-1.5 bg-pink-500 hover:bg-pink-400 disabled:opacity-60 text-white text-xs font-semibold px-3 py-1.5 rounded-lg">
                    {splittingId === r.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Scissors className="w-3 h-3" />} Import as {chapters.filter((c) => c.include).length} Lectures
                  </button>
                  <button onClick={() => setChaptersFor((prev) => { const next = { ...prev }; delete next[r.id]; return next; })} data-testid={`cancel-split-btn-${r.id}`}
                    className="text-slate-500 hover:text-slate-300"><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
